/**
 * FeatureGate — Progressive disclosure wrapper component.
 *
 * Renders children only if the user has unlocked the specified feature.
 * When the feature is locked, optionally renders a fallback (nudge card,
 * nothing, or a custom element).
 *
 * Usage:
 *   <FeatureGate feature="volunteer">
 *     <VolunteerButton />
 *   </FeatureGate>
 *
 *   <FeatureGate feature="invite_others" fallback={<LockedMessage />}>
 *     <InviteForm />
 *   </FeatureGate>
 *
 * Architecture ref: Onboarding Spec §5.2 (Progressive Disclosure)
 *
 * Follows CLAUDE.md conventions:
 *   - useAppTranslation (pages namespace)
 *   - CSS custom property tokens only
 */

import React from 'react';
import { useApiOnboarding } from '../../api/useApiOnboarding';
import { getCurrentUserGuid } from '../../api/client';
import { getIsSystemAdmin } from '../../auth/AuthContext';
import type { FeatureFlag } from '../../Data/Onboarding/archetypeConfig';

interface FeatureGateProps {
  /** The feature flag to check. */
  feature: FeatureFlag;
  /** What to render when the feature is locked. Defaults to null (hide). */
  fallback?: React.ReactNode;
  /** Children rendered when the feature is unlocked. */
  children: React.ReactNode;
}

/**
 * Conditionally renders children based on whether the user has
 * unlocked the specified feature through their archetype progression.
 *
 * If onboarding state hasn't loaded yet, renders nothing (prevents flash).
 * If the user has no archetype (legacy/admin users), renders children
 * (permissive fallback — don't block existing users).
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  fallback = null,
  children,
}) => {
  const currentUserGuid = getCurrentUserGuid();
  const { hasFeature, archetype, loading } = useApiOnboarding(currentUserGuid);

  // System admins bypass all gates (CLAUDE.md "System admins bypass all
  // permission checks"). Checked BEFORE the loading guard so an admin is
  // never redirected/hidden mid-load, and independent of onboarding archetype
  // — a backfilled 'explorer' profile must not gate an admin surface.
  if (getIsSystemAdmin()) return <>{children}</>;

  // While loading, render nothing to prevent flash
  if (loading) return null;

  // Legacy users without an archetype — permissive: show everything
  if (!archetype) return <>{children}</>;

  // Feature check
  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export default FeatureGate;
